import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import { IChartInformation } from '../interface';
// import moment from 'moment';
import {
	AddedOnIcon,
	AddInfoIcon,
	CategotyIcon,
	CurrencyUnitIcon,
	DataAvailabilityIcon,
	EndedOnIcon,
	FrequencyIcon,
	NameIcon,
	SourceIcon,
	StockExchangeIcon
} from '../components/icons/icon';
import moment from 'moment';

export const API_CONFIG = {
	baseUrl: `${process.env.REACT_APP_BASE_URL}`,
	path: {
		login: 'login',
		forget: 'password/forgot',
		reset: 'password/reset',
		getChartConfig: 'download/masterChartData',
		getChartData: 'course/courseHistoryV6',
		futuretable: 'course/furute-table-lists',
		submitImage: 'download/chartData',
		updateMasterData: 'download/masterChartData',
		basicChartData: 'course',
		chartInformation: 'materials/chart_information'
	}
};

export const getUrl = (url: string, params: any = {}): string => {
	Object.keys(params).forEach((key) => (params[key] == null || params[key] === '') && delete params[key]);
	let urlString = `${url}`;
	if (params && !isEmpty(params)) {
		urlString += `?${queryString.stringify(params)}`;
	}
	return urlString;
};

export const colorPallet = ['#4472C4', '#ED7D31', '#FFC000', '#70AD47', '#00B0F0', '#7030A0', '#FF0000', '#002060', '#000000'];

export const customNumberFormatter = (value: number, maxFractionLImit?: number) => {
	if (
		new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'DEM', maximumFractionDigits: maxFractionLImit }).format(value).includes('DEM')
	) {
		return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'DEM', maximumFractionDigits: maxFractionLImit })
			.format(value)
			.split('DEM')[0];
	}
	if (
		new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'DEM', maximumFractionDigits: maxFractionLImit }).format(value).includes('DM')
	) {
		return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'DEM', maximumFractionDigits: maxFractionLImit })
			.format(value)
			.split('DM')[0];
	}
};

export enum Alert {
	ALLALERTS = 'allAlerts',
	COURSE_EXCEEDED = 'course exceeded',
	COURSE_UNDERSHOT = 'course undershot',
	DIFFERENCE = 'abs. perc. deviation d.K. exceeded',
	KURS_EXCEEDED = 'Kurs überschritten',
	KURS_UNDERSHOT = 'Kurs unterschritten',
	KURS_DIFFERENCE = 'Abs. proz. Abweichung d. K. überschritten',
	POSITIVE_DIFFERENCE_GERMEN = '% positive Abweichung',
	POSITIVE_DIFFERENCE = '% positive deviation',
	NEGATIVE_DIFFERENCE_GERMEN = '% negative Abweichung',
	NEGATIVE_DIFFERENCE = '% negative deviation',
	OTHER_ALERTS = 'other alerts'
}

export enum alertType {
	MIN = 'min',
	MAX = 'max',
	positive = 'positive',
	negative = 'negative'
}

export const debounce = <T extends (...args: any[]) => void>(func: T, wait = 400) => {
	let timeout: NodeJS.Timeout | null = null;

	const debounced = (...args: Parameters<T>) => {
		if (timeout) {
			clearTimeout(timeout);
		}
		timeout = setTimeout(() => func(...args), wait);
	};

	debounced.cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = null;
		}
	};

	return debounced as T & { cancel: () => void };
};

const getPriceTimeForExport = (date: string) => {
	const format = 'DD.MM.YYYY';
	return moment(date).format(format);
};

/**
 * Utility function to check if a token is an admin token
 * This function decodes JWT tokens and checks for admin privileges
 */
export const isAdminToken = (token: string): boolean => {
	if (!token) return false;

	try {
		// Decode JWT token (format: header.payload.signature)
		const parts = token.split('.');
		if (parts.length !== 3) {
			return false; // Not a valid JWT
		}

		// Decode the payload (second part)
		const payload = JSON.parse(atob(parts[1]));

		// Check if admin field exists and has admin privileges
		if (payload.admin && Array.isArray(payload.admin)) {
			// Check for any admin role (superAdmin, admin, etc.)
			return payload.admin.length > 0;
		}

		return false;
	} catch (error) {
		// Token is not JWT or invalid
		console.error('Error decoding JWT token:', error);
		return false;
	}
};

export const chartInfoTableData = (information: IChartInformation, language: string, chartUpdate: { [key: string]: string }, token: string) => {
	let currencyValue = '';
	if (information.waehrung === '' && information.einheit === '') {
		currencyValue = '-';
	} else if (information.waehrung === '') {
		currencyValue = `- / ${information.einheit}`;
	} else if (information.einheit === '') {
		currencyValue = `${information.waehrung} / -`;
	} else {
		currencyValue = `${information.waehrung} / ${information.einheit}`;
	}

	// Base table data without Source column
	const baseTableData = [
		{
			label: `${language === 'en' ? 'Name' : 'Name'} `,
			value:
				(language === 'en' && information.name_eng !== '' && information.name_eng) ||
				(information.name_deu !== '' ? information.name_deu : information.name),
			Icon: NameIcon
		},
		{
			label: `${language === 'en' ? 'Category' : 'Kategorie'}`,
			value: (!isEmpty(information.category) && information.category.join(' , ')) || '-',
			Icon: CategotyIcon
		},
		{
			label: `${language === 'en' ? 'Frequency' : 'Frequenz'} `,
			value: chartUpdate[information.notierung] || '-',
			Icon: FrequencyIcon
		},
		{
			label: `${language === 'en' ? 'Original' : 'Original'} ${language === 'en' ? 'Currency' : 'Währung'} / ${
				language === 'en' ? 'Unit' : 'Einheit'
			}`,
			value: currencyValue,
			Icon: CurrencyUnitIcon
		},
		{
			label: `${language === 'en' ? 'Stock exchange' : 'Börse'} `,
			value: information.boersenname || '-',
			Icon: StockExchangeIcon
		}
	];

	// Check if token is admin token
	const isAdmin = isAdminToken(token);

	// Add Source column only for admin tokens
	if (isAdmin) {
		baseTableData.push({
			label: `${language === 'en' ? 'Source' : 'Quelle'} `,
			value: information.quelle || '-',
			Icon: SourceIcon
		});
	}

	// Add remaining columns
	baseTableData.push(
		{
			label: `${language === 'en' ? 'Data availability' : 'Datenverfügbarkeit'} `,
			value: `${information.oldestDate ? getPriceTimeForExport(information.oldestDate) : '-'} - ${
				information.newestDate ? getPriceTimeForExport(information.newestDate) : '-'
			}`,
			Icon: DataAvailabilityIcon
		},
		{
			label: `${language === 'en' ? 'Add. info' : 'Zus. Informationen'} `,
			value: language === 'en' ? information.notierung_zusatz_en : information.notierung_zusatz || '-',
			Icon: AddInfoIcon
		},
		{
			label: `${language === 'en' ? 'Added on' : 'Hinzugefügt am'} `,
			value: (information.hinzugefuegt_am && getPriceTimeForExport(information.hinzugefuegt_am)) || '-',
			Icon: AddedOnIcon
		},
		{
			label: `${language === 'en' ? 'Ended on' : 'Eingestellt am'} `,
			value: (information.entfernt_am && getPriceTimeForExport(information.entfernt_am)) || '-',
			Icon: EndedOnIcon
		}
	);

	return baseTableData;
};
export const chartColors = ['#FF9100', '#8CEA80', '#A81FF8'];

export enum ForeCastType {
	FORECASTED_PRICE = 'forecastedPrice',
	ACTUAL_PRICE = 'actualPrice'
}

export enum ForecastName {
	MATFLIXX_FORECAST = 'Matflixx forecast',
	MATFLIXX_AI_FORECAST = 'Matflixx AI forecast'
}

const getDifferenceDaysForOneYear = (endDate: any) => {
	const currentYear = endDate.getFullYear();
	const startDate = new Date(currentYear, 0, 1);
	const Difference_In_Time = endDate.getTime() - startDate.getTime();
	return Math.round(Difference_In_Time / (1000 * 3600 * 24));
};

export const chartTimeRangeMapper = (chartData: any): { [key: string]: number } => {
	if (!isEmpty(chartData)) {
		return {
			'1w': 7,
			'1m': 30,
			'3m': 90,
			'6m': 180,
			'1y': 365,
			'3y': 1095,
			'5y': 1825,
			'10y': 3650,
			ytd: getDifferenceDaysForOneYear(new Date(chartData[chartData.length - 1].date)),
			max: chartData.length
		};
	} else {
		return {};
	}
};

export const formatDate = (date: any, format?: string) => {
	if (!date) {
		return '';
	}
	return moment(date)
		.local()
		.format(format?.toUpperCase() || 'DD-MM-YYYY');
};

export const barChartFrequencyMapper = (frequency: string | null) => {
	switch (frequency) {
		case 'Y':
			return '1y';
		case 'Q':
			return '1m';
		case 'M':
			return '1m';
		case 'W':
			return '1w';
		case 'D':
			return '1d';
		case 'B':
			return '1d';
		case '2':
			return '1m';
		case 'H':
			return '1d';
		case 'I':
			return '1d';
		case 'C':
			return '1d';
		default:
			return '1y';
	}
};